/** SCENARIILE DE BUSINESS CASE ale proiectului: aceleași măsurători, alte
 *  ipoteze. Se țin pe server (`control-plane/api/scenarii.py`), ca un scenariu
 *  făcut azi să fie acolo mâine și pentru colegul care deschide pagina.
 *
 *  UN SCENARIU E O LISTĂ DE ABATERI, nu o copie a ipotezelor. Ce nu e scris pe
 *  el urmează proiectul (`ipotezeDin`) și reperele zilei; altfel un reper
 *  corectat mâine ar rămâne vechi în fiecare scenariu salvat azi.
 *
 *  CINE SCHIMBĂ, CUM. Omul își corectează scenariul curent pe loc, din ecranul
 *  «Ipoteze». Asistentul nu-l atinge: face un scenariu NOU din cel curent, cu
 *  schimbările cerute, și îl pune curent — cel vechi rămâne în listă, ca să se
 *  poată compara și ca omul să se poată întoarce la el dintr-un clic.
 *
 *  REȚETELE ȘI PREȚURILE PE SCENARIU. `catalog` ține ce schimbă scenariul în
 *  catalogul listei de cantități (numai diferențele față de proiect, v.
 *  `boq.ts::suprascrierile`); lista scenariului o face serverul. O ofertă
 *  trasă pe pagina «Oferte» face un scenariu cu `autor: "oferta"`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, get, put } from "../../api";
import type { Ipoteze } from "./businessCase";
import type { Suprascrieri } from "./boq";
import type { SetariProgram } from "./program";

/** Abaterile unui scenariu: orice ipoteză numerică, plus ajustările pe rânduri. */
export type Abateri = Partial<Record<Exclude<keyof Ipoteze, "ajustari">, number>>
  & { ajustari?: Record<string, number> };

export type Scenariu = {
  id: string;
  nume: string;
  creat_la: string;
  autor: "om" | "asistent" | "oferta";
  ipoteze: Abateri;
  /** Ce schimbă scenariul în rețetele și prețurile proiectului. */
  catalog?: Suprascrieri | null;
  /** Oferta din care s-a făcut (`api/oferte.py`). */
  oferta?: string | null;
  /** Ce schimbă scenariul în programul de execuție (efective, lag-uri, cicluri)
   *  peste setările proiectului (`api/program.py`). */
  program?: SetariProgram | null;
  /** De ce există — cererea omului, în cuvintele asistentului. */
  nota?: string;
  /** Scenariul din care s-a făcut. */
  parinte?: string | null;
};

export type StareScenarii = {
  rev: number;
  curent: string | null;
  scenarii: Scenariu[];
  /** Cine a confirmat ipotezele, și când; `null` cât nu le-a confirmat nimeni. */
  verificate: Semnatura | null;
  /** Ipotezele confirmate una câte una, cu semnătura fiecăreia; lipsește pe
   *  serverele de dinaintea validării pe rând. */
  validate?: Record<string, Semnatura>;
};

export type Semnatura = { de: string; la: string };

/** Cele mai multe scenarii păstrate (plafonul serverului). Peste el, cele mai
 *  vechi făcute de asistent pleacă primele. */
export const MAX_SCENARII = 60;

const cale = (projectId: string) => `/api/projects/${projectId}/costuri/scenarii`;

export const getScenarii = (projectId: string) => get<StareScenarii>(cale(projectId));

export const putScenarii = (projectId: string, s: Omit<StareScenarii, "verificate" | "validate">
                            & { verificate: boolean; validate: string[] }) =>
  put<StareScenarii>(cale(projectId), s);

/** Un id scurt, unic în listă. */
function idNou(lista: Scenariu[]): string {
  for (;;) {
    const id = Math.random().toString(36).slice(2, 10);
    if (!lista.some((s) => s.id === id)) return id;
  }
}

/** PRIMUL SCENARIU, când proiectul n-are niciunul: ipotezele implicite, fără
 *  nicio abatere. Se scrie pe server abia la prima schimbare. */
export function scenariulDeBaza(nume: string): Scenariu {
  return { id: "baza", nume, creat_la: new Date().toISOString(), autor: "om", ipoteze: {} };
}

export function scenariulCurent(s: StareScenarii | null, numeBaza: string): Scenariu {
  return s?.scenarii.find((x) => x.id === s.curent) ?? s?.scenarii[0]
    ?? scenariulDeBaza(numeBaza);
}

/** Abaterile `a` cu `b` peste; un factor 1 sau o valoare egală cu cea
 *  implicită nu se mai ține — scenariul rămâne lista a ce e altfel.
 *  Implicitele (ipotezele proiectului fără nicio abatere) vin de la server cu
 *  rezumatul; până sosesc, orice abatere se ține. */
export function cuAbateri(a: Abateri, b: Abateri, implicite: Partial<Ipoteze>): Abateri {
  const { ajustari: ajA, ...restA } = a;
  const { ajustari: ajB, ...restB } = b;
  const out: Abateri = {};
  for (const [k, v] of Object.entries({ ...restA, ...restB })) {
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    if ((implicite as Record<string, unknown>)[k] === v) continue;
    (out as Record<string, number>)[k] = v;
  }
  const aj: Record<string, number> = {};
  for (const [k, v] of Object.entries({ ...(ajA ?? {}), ...(ajB ?? {}) })) {
    if (typeof v === "number" && Number.isFinite(v) && v !== 1) aj[k] = v;
  }
  if (Object.keys(aj).length) out.ajustari = aj;
  return out;
}

/** Lista cu un scenariu nou la capăt; peste plafon pleacă cel mai vechi al
 *  asistentului care nu e cel curent (și nici cel nou). */
export function cuScenariuNou(lista: Scenariu[], nou: Scenariu, curent: string | null): Scenariu[] {
  const out = [...lista, nou];
  while (out.length > MAX_SCENARII) {
    const k = out.findIndex((s) => s.autor !== "om" && s.id !== curent && s.id !== nou.id);
    if (k < 0) break;
    out.splice(k, 1);
  }
  return out;
}

export type Scenarii = {
  /** `null` până răspunde serverul. */
  stare: StareScenarii | null;
  curent: Scenariu;
  /** Ultima greșeală la salvare, spusă omului. */
  eroare: string | null;
  seSalveaza: boolean;
  /** Abaterile scenariului curent, schimbate pe loc (ecranul «Ipoteze»). */
  schimba: (abateri: Abateri) => Promise<void>;
  /** Un scenariu nou din cel curent, pus curent; întoarce id-ul lui. Cu
   *  `program`, scenariul primește suprascrierile de program date (altfel le
   *  moștenește pe ale celui din care se face). */
  creeaza: (abateri: Abateri, meta: { nume: string; autor: Scenariu["autor"]; nota?: string; program?: SetariProgram })
    => Promise<Scenariu>;
  alege: (id: string) => Promise<void>;
  sterge: (id: string) => Promise<void>;
  redenumeste: (id: string, nume: string) => Promise<void>;
  /** Confirmă sau retrage toate ipotezele odată; retragerea scoate și
   *  validările pe rând. */
  verifica: (da: boolean) => Promise<void>;
  /** Confirmă o singură ipoteză; `ultima` = era singura rămasă, deci se
   *  confirmă și ansamblul. */
  valideaza: (cheie: string, ultima: boolean) => Promise<void>;
  /** Rețetele și prețurile unui scenariu (suprascrierile față de proiect). */
  schimbaCatalog: (id: string, catalog: Suprascrieri) => Promise<void>;
  /** Citește iar de pe server (după ce serverul a făcut un scenariu dintr-o ofertă). */
  reincarca: () => Promise<void>;
};

/** STAREA SCENARIILOR, cu salvarea ei. Fiecare schimbare pleacă întreagă, cu
 *  `rev`: dacă între timp a scris altcineva, serverul răspunde 409 cu ce e acolo,
 *  ecranul trece pe aceea și spune — nu calcă peste colegul. */
export function useScenarii(projectId: string, implicite: Partial<Ipoteze> | (() => Partial<Ipoteze>), numeBaza: string,
                            mesajCalcat: string): Scenarii {
  const [stare, setStare] = useState<StareScenarii | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const [seSalveaza, setSeSalveaza] = useState(false);
  // ultima stare cunoscută, citită de funcțiile de mai jos fără să le refacă
  // la fiecare schimbare (uneltele asistentului le țin de la deschidere)
  const ref = useRef<StareScenarii | null>(null);
  ref.current = stare;
  // implicitele pot veni și ca funcție: pagina le socotește din lista de
  // cantități a scenariului curent, deci după ce află scenariul
  const implRef = useRef(implicite);
  implRef.current = implicite;
  const impl = useMemo(() => ({ get current(): Partial<Ipoteze> {
    const i = implRef.current;
    return typeof i === "function" ? i() : i;
  } }), []);

  const reincarca = useCallback(async () => {
    const s = await getScenarii(projectId);
    setStare(s);
    ref.current = s;
  }, [projectId]);

  useEffect(() => {
    let viu = true;
    getScenarii(projectId)
      .then((s) => { if (viu) setStare(s); })
      .catch(() => {
        // fără server (sau fără rută, pe un mediu nedeployat), pagina merge pe
        // implicite și nu salvează nimic
        if (viu) setStare({ rev: 0, curent: null, scenarii: [], verificate: null });
      });
    return () => { viu = false; };
  }, [projectId]);

  const scrie = useCallback(async (urmator: StareScenarii) => {
    const inainte = ref.current;
    setStare(urmator);
    ref.current = urmator;
    setSeSalveaza(true);
    try {
      const r = await putScenarii(projectId, {
        rev: urmator.rev, curent: urmator.curent, scenarii: urmator.scenarii,
        verificate: !!urmator.verificate, validate: Object.keys(urmator.validate ?? {}) });
      setStare(r);
      ref.current = r;
      setEroare(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.date?.actual) {
        const actual = e.date.actual as StareScenarii;
        setStare(actual);
        ref.current = actual;
        setEroare(mesajCalcat);
      } else {
        setStare(inainte);
        ref.current = inainte;
        setEroare((e as Error).message);
      }
      throw e;
    } finally {
      setSeSalveaza(false);
    }
  }, [projectId, mesajCalcat]);

  /** Starea cu scenariul de bază scris în listă, dacă lista era goală. */
  const cuBaza = useCallback((): StareScenarii => {
    const s = ref.current ?? { rev: 0, curent: null, scenarii: [], verificate: null };
    if (s.scenarii.length) return s;
    const b = scenariulDeBaza(numeBaza);
    return { ...s, scenarii: [b], curent: b.id };
  }, [numeBaza]);

  const schimba = useCallback(async (abateri: Abateri) => {
    const s = cuBaza();
    const id = s.curent ?? s.scenarii[0].id;
    await scrie({ ...s, curent: id, scenarii: s.scenarii.map((x) => x.id === id
      ? { ...x, ipoteze: cuAbateri(x.ipoteze, abateri, impl.current) } : x) });
  }, [cuBaza, scrie]);

  const creeaza = useCallback(async (abateri: Abateri,
                                     meta: { nume: string; autor: Scenariu["autor"]; nota?: string;
                                             program?: SetariProgram }) => {
    const s = cuBaza();
    const din = s.scenarii.find((x) => x.id === s.curent) ?? s.scenarii[0];
    const program = meta.program ?? din.program;
    const nou: Scenariu = {
      id: idNou(s.scenarii), nume: meta.nume.slice(0, 120), creat_la: new Date().toISOString(),
      autor: meta.autor, ipoteze: cuAbateri(din.ipoteze, abateri, impl.current),
      // rețetele, prețurile și programul scenariului din care se face rămân
      ...(din.catalog ? { catalog: din.catalog } : {}),
      ...(program && Object.keys(program).length ? { program } : {}),
      ...(meta.nota ? { nota: meta.nota.slice(0, 600) } : {}), parinte: din.id,
    };
    await scrie({ ...s, curent: nou.id, scenarii: cuScenariuNou(s.scenarii, nou, s.curent) });
    return nou;
  }, [cuBaza, scrie]);

  const alege = useCallback(async (id: string) => {
    const s = cuBaza();
    if (!s.scenarii.some((x) => x.id === id) || s.curent === id) return;
    await scrie({ ...s, curent: id });
  }, [cuBaza, scrie]);

  const sterge = useCallback(async (id: string) => {
    const s = cuBaza();
    if (s.curent === id || s.scenarii.length < 2) return;
    await scrie({ ...s, scenarii: s.scenarii.filter((x) => x.id !== id) });
  }, [cuBaza, scrie]);

  const redenumeste = useCallback(async (id: string, nume: string) => {
    const s = cuBaza();
    const n = nume.trim().slice(0, 120);
    if (!n) return;
    await scrie({ ...s, scenarii: s.scenarii.map((x) => x.id === id ? { ...x, nume: n } : x) });
  }, [cuBaza, scrie]);

  const verifica = useCallback(async (da: boolean) => {
    const s = cuBaza();
    await scrie({ ...s, verificate: da ? (s.verificate ?? { de: "", la: "" }) : null,
                  validate: da ? s.validate : {} });
  }, [cuBaza, scrie]);

  // semnătura goală e doar locul cheii: numele și ora le pune serverul
  const valideaza = useCallback(async (cheie: string, ultima: boolean) => {
    const s = cuBaza();
    await scrie({ ...s, validate: { ...(s.validate ?? {}), [cheie]: s.validate?.[cheie] ?? { de: "", la: "" } },
                  verificate: ultima ? (s.verificate ?? { de: "", la: "" }) : s.verificate });
  }, [cuBaza, scrie]);

  const schimbaCatalog = useCallback(async (id: string, catalog: Suprascrieri) => {
    const s = cuBaza();
    const gol = !Object.keys(catalog).length;
    await scrie({ ...s, scenarii: s.scenarii.map((x) => {
      if (x.id !== id) return x;
      const { catalog: _vechi, ...rest } = x;
      return gol ? rest : { ...rest, catalog };
    }) });
  }, [cuBaza, scrie]);

  return { stare, curent: scenariulCurent(stare, numeBaza), eroare, seSalveaza,
           schimba, creeaza, alege, sterge, redenumeste, verifica, valideaza, schimbaCatalog, reincarca };
}
